import { useState, useEffect } from 'react';
import { insightsApi, filesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Sparkles, 
  AlertTriangle, 
  TrendingUp, 
  Lightbulb,
  Target,
  Trash2,
  RefreshCw,
  Loader2,
  Clock
} from 'lucide-react';

const insightIcons = {
  warning: AlertTriangle,
  opportunity: Lightbulb,
  trend: TrendingUp,
  recommendation: Target,
};

const insightColors = {
  warning: 'border-amber-200 bg-amber-50',
  opportunity: 'border-emerald-200 bg-emerald-50',
  trend: 'border-blue-200 bg-blue-50',
  recommendation: 'border-violet-200 bg-violet-50',
};

const insightIconColors = {
  warning: 'text-amber-600',
  opportunity: 'text-emerald-600',
  trend: 'text-blue-600',
  recommendation: 'text-violet-600',
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const InsightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [insights, setInsights] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [insightsRes, filesRes] = await Promise.all([
        insightsApi.getAll(),
        filesApi.getAll()
      ]);
      setInsights(insightsRes.data);
      setFiles(filesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const response = await insightsApi.generate(selectedFile || null);
      setInsights(prev => [...response.data.insights, ...prev]);
      toast.success('AI insights generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate insights');
    } finally {
      setGenerating(false);
    }
  };

  const deleteInsight = async (id) => {
    try {
      await insightsApi.delete(id);
      setInsights(prev => prev.filter(i => i.id !== id));
      toast.success('Insight deleted');
    } catch (error) {
      toast.error('Failed to delete insight');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="insights-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="insights-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-light text-slate-900">AI Insights</h1>
          <p className="text-slate-500 mt-1">AI-powered financial analysis and recommendations</p>
        </div>
      </div>

      {/* Generate Section */}
      <Card className="border border-violet-200 bg-violet-50/50 shadow-none rounded-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-violet-600" strokeWidth={1.5} />
                <h3 className="font-display text-lg font-medium text-violet-900">Generate New Insights</h3>
              </div>
              <p className="text-sm text-violet-700">
                Let our AI analyze your financial data and provide actionable insights
              </p>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Select value={selectedFile || "all"} onValueChange={(v) => setSelectedFile(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[220px] rounded-sm bg-white" data-testid="file-select">
                  <SelectValue placeholder="Select a file (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All data</SelectItem>
                  {files.map(file => (
                    <SelectItem key={file.id} value={file.id}>
                      {file.filename.slice(0, 30)}...
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={generateInsights}
                disabled={generating}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm"
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights List */}
      {insights.length > 0 ? (
        <div className="space-y-4">
          {insights.map((insight) => {
            const Icon = insightIcons[insight.insight_type] || Target;
            const colorClass = insightColors[insight.insight_type] || insightColors.recommendation;
            const iconColor = insightIconColors[insight.insight_type] || insightIconColors.recommendation;
            
            return (
              <Card 
                key={insight.id} 
                className={`border shadow-none rounded-sm ${colorClass}`}
                data-testid={`insight-${insight.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center bg-white/80 ${iconColor}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-display text-lg font-medium text-slate-900">
                            {insight.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 bg-white/80 rounded-sm capitalize text-slate-600">
                              {insight.insight_type}
                            </span>
                            {insight.related_data?.impact && (
                              <span className={`text-xs px-2 py-0.5 rounded-sm capitalize ${
                                insight.related_data.impact === 'high' 
                                  ? 'bg-red-100 text-red-700'
                                  : insight.related_data.impact === 'medium'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {insight.related_data.impact} impact
                              </span>
                            )}
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => deleteInsight(insight.id)}
                          className="text-slate-400 hover:text-red-600 rounded-sm"
                          data-testid={`delete-insight-${insight.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                      <p className="text-slate-700 mt-3 whitespace-pre-wrap">
                        {insight.content}
                      </p>
                      <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(insight.created_at)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border border-slate-200 shadow-none rounded-sm">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-medium text-slate-900 mb-2">
              No insights yet
            </h3>
            <p className="text-slate-500 mb-4">
              Upload financial data and generate AI-powered insights
            </p>
            <Button 
              onClick={generateInsights}
              disabled={generating}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm"
            >
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Generate First Insights
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InsightsPage;
